import { jsx as _jsx } from "react/jsx-runtime";
import { CAROUSEL_LAYER_IDS, CAROUSEL_SCENES_KEY, CAROUSEL_SCENE_KIND_KEY, carouselSceneKind, carouselSlotKey, } from "@diality/contracts";
import { Section } from "./_section";
import { Carousel, } from "../components/blocks/carousel";
/**
 * CarouselBlock — the crossfading band of scenes, composed in the CMS.
 *
 * The component itself is untouched by authoring: it takes the same `slides` and
 * `label` whether the Product page hands them to it in code or an Editor
 * composes them. This file is the whole of the translation.
 *
 * A SCENE'S KIND IS THE WHOLE ARRANGEMENT. It names an ordered list of baked
 * positions, and the picture stored against a position IS the picture drawn
 * there — nothing is zipped by array order, so a mapper cannot put the front
 * subject behind the back one.
 */
/**
 * The two spellings of a layer id must agree, and this is where that is proven.
 *
 * `@diality/contracts` owns the list because the schema is built from it; the
 * component owns a union because its paint-order table is keyed by it. Neither
 * can import the other's, so both directions are asserted here instead — an id
 * added to one and not the other stops this file compiling rather than shipping
 * a layer that lands unpositioned.
 */
const _contractsIdsAreComponentIds = [
    ...CAROUSEL_LAYER_IDS,
];
const _bothDirectionsHold = true;
void _contractsIdsAreComponentIds;
void _bothDirectionsHold;
/**
 * What a carousel is called before it has been named.
 *
 * The name is required to SAVE, but the CMS canvas draws a block while it is
 * still being filled in, and an unnamed region is worse for a screen-reader user
 * than a generic one. Never reached by published content.
 */
const UNNAMED_CAROUSEL = "Carousel";
/** The bare band, with no section chrome — what a component harness renders. */
export function CarouselBlockView({ data }) {
    return _jsx(Carousel, { label: data.label, slides: data.slides });
}
export function CarouselBlock({ data }) {
    return (_jsx(Section, { className: "w-full overflow-x-clip", children: _jsx(CarouselBlockView, { data: data }) }));
}
/**
 * The authored shape: a name, and a list of scenes each carrying its kind.
 *
 * A scene whose kind is unrecognised — or absent, which is what the canvas's
 * own placeholder item looks like — renders its COPY AND NO ARTWORK rather than
 * disappearing. Dropping the scene would be the silent drop this component's
 * rules exist to prevent, and there is nowhere to place artwork for an
 * arrangement nothing knows.
 *
 * A layer whose picture has not been chosen yet is left out of the scene. The
 * validator refuses to SAVE a scene that is short a picture, so this only ever
 * happens mid-compose — and an `<img>` with no address makes the browser refetch
 * the document, which would wedge the very decode gate that governs whether the
 * carousel will move at all.
 */
export function carouselFromBlock(block, resolve) {
    const items = block.repeats?.[CAROUSEL_SCENES_KEY] ?? [];
    const slides = items.map((item, index) => {
        const kind = carouselSceneKind(item.scalars?.[CAROUSEL_SCENE_KIND_KEY]);
        const title = item.scalars?.title?.trim() ?? "";
        const body = item.scalars?.description ?? "";
        const layers = (kind?.layers ?? [])
            .map((layerId) => {
            const desktop = resolve(item.media?.[carouselSlotKey(layerId, "desktop")]);
            const mobile = resolve(item.media?.[carouselSlotKey(layerId, "mobile")]);
            const src = desktop?.url ?? mobile?.url;
            if (!src)
                return null;
            return {
                id: layerId,
                src,
                mobileSrc: mobile?.url ?? src,
                // A scene is ONE composed picture cut into transparent pieces, and it
                // is already named twice — by the slide's own label and by the live
                // region. Naming each piece would make a screen-reader user hear three
                // fragments of a thing they have already been told about.
                alt: "",
            };
        })
            .filter((layer) => layer !== null);
        return {
            id: item.id ?? `scene-${index + 1}`,
            // The scene's own description for assistive technology. Derived from the
            // title because the title is the only authored words that describe the
            // scene — and a scene that is not yet titled says which of them it is.
            sceneLabel: title || `Scene ${index + 1}`,
            title: [title],
            body,
            layers,
        };
    });
    return {
        label: block.scalars?.label?.trim() || UNNAMED_CAROUSEL,
        slides,
    };
}
export const carouselBlockEntry = {
    Component: CarouselBlock,
    fromBlock: carouselFromBlock,
};
//# sourceMappingURL=carousel.js.map